import { useAppContext } from "@/contexts/AppContext";
import { BfEventType, BfMapMarkerType } from "@/lib/api/models";
import { DEBUG_MODE } from "@/lib/client";
import { KNOWN_MARKERS, uiMarkers } from "@/lib/markers";
import Handlebars from "handlebars";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { HandlebarsTemplateType } from "./SidebarEnums";

interface MustacheTemplateProps {
    eventType: BfEventType;
    event: object;
    fallback?: ReactNode | null | undefined;
    templateType?: HandlebarsTemplateType;
}

// eslint-disable-next-line react-refresh/only-export-components
export const compileTemplate = (
    template: string | null | undefined,
    data: object
): string | null => {
    if (!template) {
        return null;
    }
    const compiled = Handlebars.compile(template);
    const output = compiled(data);
    if (DEBUG_MODE) {
        console.debug("Compiled Handlebars template:", { template, data, output });
    }
    return output;
};

const resolveOutlinedMarkers = async (html: string): Promise<string> => {
    const markers = uiMarkers();
    if (!markers) return html;

    const container = window.document.createElement("div");
    container.innerHTML = html;
    const imgs = Array.from(
        container.querySelectorAll<HTMLImageElement>("img[data-bf-marker][data-bf-outlined]")
    );

    await Promise.all(
        imgs.map(async (img) => {
            const markerType = Object.values(BfMapMarkerType).find(
                (marker) => marker === img.dataset.bfMarker
            );
            if (!markerType) return;

            const variant = img.dataset.bfOutlined === "predicted" ? "predicted" : "auto";
            const url = await markers.outlinedDataUrl(markerType, variant);
            if (url) img.setAttribute("src", url);
        })
    );

    return container.innerHTML;
};

/// Resolve {{marker ... outlined=...}} images in compiled Handlebars HTML to their
/// outlined variants. Returns the input until the variants are generated.
// eslint-disable-next-line react-refresh/only-export-components
export const useOutlinedMarkerHtml = (rawHtml: string | null): string | null => {
    const [resolved, setResolved] = useState<{ raw: string; html: string } | null>(null);

    useEffect(() => {
        if (!rawHtml || !rawHtml.includes("data-bf-outlined")) return;

        let active = true;
        resolveOutlinedMarkers(rawHtml)
            .then((html) => {
                if (active) setResolved({ raw: rawHtml, html });
            })
            .catch((e) => {
                console.warn("Failed to resolve outlined markers", e);
            });

        return () => {
            active = false;
        };
    }, [rawHtml]);

    if (!rawHtml) return null;
    return resolved?.raw === rawHtml ? resolved.html : rawHtml;
};

export const HandlebarsTemplate = ({
    event,
    eventType,
    fallback,
    templateType,
}: MustacheTemplateProps) => {
    const { unitSystem, user, getTemplateForLayerGroup } = useAppContext();
    const data = useMemo(
        () => ({
            client: {
                language: typeof navigator !== "undefined" ? navigator.language : "en",
            },
            user,
            unitSystem,
            event: event,
        }),
        [user, unitSystem, event]
    );

    const containerRef = useRef<HTMLSpanElement | null>(null);

    const template = getTemplateForLayerGroup(eventType, unitSystem);

    const compiledHtml = useMemo(() => {
        if (!template) return null;

        try {
            switch (templateType) {
                case HandlebarsTemplateType.Title: {
                    return compileTemplate(template.title, data);
                }
                case HandlebarsTemplateType.Body: {
                    return compileTemplate(template.template, data);
                }
                case HandlebarsTemplateType.Subtitle: {
                    return compileTemplate(template.subtitle, data);
                }
                default:
                    return null;
            }
        } catch (e) {
            console.warn("HandlebarsTemplate compile error", e);
            return null;
        }
    }, [template, data, templateType]);

    const html = useOutlinedMarkerHtml(compiledHtml);

    useEffect(() => {
        const root = containerRef.current;
        if (!root) return;
        if (!html) return;

        const imgs = Array.from(root.querySelectorAll<HTMLImageElement>("img[data-bf-refresh]"));

        const timers: number[] = [];

        imgs.forEach((img) => {
            const intervalMs = Number(img.dataset.bfRefresh) * 1000;
            if (!intervalMs || Number.isNaN(intervalMs)) return;

            const originalSrc = img.getAttribute("src") ?? "";

            const refresh = () => {
                const url = new URL(originalSrc, window.location.href);
                url.searchParams.set("_t", Math.floor(Date.now() / 10000).toString());

                // Preload the new image before swapping
                const preloadImg = new Image();
                preloadImg.onload = () => {
                    img.src = url.toString();
                };
                preloadImg.onerror = () => {
                    // If the new image fails to load, keep the old one
                    console.warn("Failed to refresh image:", url.toString());
                };
                preloadImg.src = url.toString();
            };

            const id = window.setInterval(refresh, intervalMs);
            timers.push(id);
        });

        return () => {
            timers.forEach((id) => window.clearInterval(id));
        };
    }, [html]);

    // Referentially stable: React 19 diffs the wrapper object, not the string,
    // and re-assigns innerHTML (nuking data-bf-refresh img swaps) when it changes.
    const innerHtml = useMemo(() => (html ? { __html: html } : null), [html]);

    if (!innerHtml) {
        return <>{fallback ?? null}</>;
    }

    return <span ref={containerRef} dangerouslySetInnerHTML={innerHtml} />;
};

export const InitHandlebarsHelpers = () => {
    // Range accepts something like if {{#range x 1 10}} then show {{this}} {{/range}}
    Handlebars.registerHelper("range", function (val, start, end, block) {
        if (val >= start && val < end) {
            return block.fn();
        }
    });
    Handlebars.registerHelper("eq", function (a, b) {
        return a === b;
    });
    Handlebars.registerHelper("ne", function (a, b) {
        return a !== b;
    });
    Handlebars.registerHelper("gt", function (a, b) {
        return a > b;
    });
    Handlebars.registerHelper("gte", function (a, b) {
        return a >= b;
    });
    Handlebars.registerHelper("lt", function (a, b) {
        return a < b;
    });
    Handlebars.registerHelper("lte", function (a, b) {
        return a <= b;
    });
    Handlebars.registerHelper("titlecase", function (str) {
        if (typeof str !== "string") return str;
        return str.toLowerCase().replace(/\b\w/g, (s) => s.toUpperCase());
    });
    // Inline marker image: {{marker "incidentFireUncontrolled"}}, optionally
    // outlined=true (purple "auto") or outlined="predicted" (pink)
    Handlebars.registerHelper("marker", function (type, options) {
        const markerType = Object.values(BfMapMarkerType).find((marker) => marker === type);
        if (!markerType || !KNOWN_MARKERS[markerType]?.payload) return "";

        const escapedType = Handlebars.escapeExpression(markerType);
        const requested = options?.hash?.outlined;
        const variant =
            requested === true || requested === "auto"
                ? "auto"
                : requested === "predicted"
                  ? "predicted"
                  : null;
        const outlined = variant
            ? ` data-bf-marker="${escapedType}" data-bf-outlined="${variant}"`
            : "";

        return new Handlebars.SafeString(
            `<img src="${KNOWN_MARKERS[markerType].payload}" alt="${escapedType}" ` +
                `class="tw:w-10 tw:inline-block tw:align-middle"${outlined} />`
        );
    });
};
